import * as yup from 'yup';
import type { InvoiceDataViewDto } from '@subscriptions/application/dtos';
import { CfdiUses } from '@subscriptions/domain/types';
import { matchSocietyRegime, validateTaxId, rfcTypes } from '@subscriptions/presentation/utils';

export const DEFAULT_CFDI_VALUE = CfdiUses.GENERAL_EXPENSES;

export const INVOICE_DATA_MAX_LENGTHS = {
  businessName: 300,
  firstName: 150,
  firstLastName: 150,
  secondLastName: 150,
} as const;

export type InvoiceDataFormValues = {
  businessName: string;
  firstName: string;
  firstLastName: string;
  secondLastName: string;
  postalCode: string;
  rfc: string;
  cfdi: string;
  email: string;
  taxRegimeSatCode: string;
};

export type InvoicePersonType = 'person' | 'company' | null;

const RFC_REGEX = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i;
const POSTAL_CODE_REGEX = /^\d{5}$/;

/**
 * Resolves the invoice person type from an RFC value.
 *
 * @param rfc - RFC input value
 * @returns Person type inferred from RFC or null when invalid
 */
export const getInvoicePersonType = (rfc: string): InvoicePersonType => {
  const { isValid, type } = validateTaxId(rfc);

  if (!isValid) {
    return null;
  }

  if (type === rfcTypes.COMPANY) {
    return 'company';
  }

  if (type === rfcTypes.PERSON) {
    return 'person';
  }

  return null;
};

/**
 * Checks whether the RFC is the Mexican "público general" generic RFC (XAXX010101000).
 *
 * Invoice data cannot be created for it: the form surfaces the `rfcGeneric` error and
 * hides the remaining fields. Mirrors the schema's `not-generic-rfc` test condition.
 *
 * @param rfc - RFC input value
 * @returns true when the RFC is the público general generic RFC
 */
export const isPublicoGeneralRfc = (rfc: string): boolean => validateTaxId(rfc, true).type === rfcTypes.GENERIC;

/**
 * Maps an InvoiceDataViewDto to the form values model used by the edit flow.
 *
 * @param invoiceData - Invoice data view DTO
 * @returns Form values pre-filled from the view DTO
 */
export const mapViewDtoToFormValues = (invoiceData: InvoiceDataViewDto): InvoiceDataFormValues => ({
  businessName: invoiceData.businessName,
  firstName: invoiceData.firstName,
  firstLastName: invoiceData.firstLastName,
  secondLastName: invoiceData.secondLastName,
  postalCode: invoiceData.postalCode,
  rfc: invoiceData.rfc,
  cfdi: invoiceData.cfdi,
  email: invoiceData.email,
  taxRegimeSatCode: invoiceData.taxRegime,
});

/**
 * Removes company suffixes from business names to preserve the existing invoicing behavior.
 *
 * @param businessName - Business name entered by the user
 * @returns Sanitized business name
 */
export const sanitizeBusinessName = (businessName: string): string => {
  const normalizedBusinessName = businessName.trim().toUpperCase();
  const societyMatch = matchSocietyRegime(normalizedBusinessName);

  if (!societyMatch.isMatch) {
    return normalizedBusinessName;
  }

  return normalizedBusinessName.replace(societyMatch.replaceWord, '').trim();
};

/**
 * Creates the validation schema for the invoice data form.
 *
 * @param t - Translation function
 * @returns Yup schema for invoice data creation
 */
export const createInvoiceDataFormSchema = (t: (key: string, options?: Record<string, unknown>) => string) =>
  yup.object({
    businessName: yup
      .string()
      .trim()
      .max(INVOICE_DATA_MAX_LENGTHS.businessName, ({ max }) => t('invoiceDataForm.validation.maxLength', { max }))
      .when('rfc', {
        is: (rfc: string) => getInvoicePersonType(rfc) === 'company',
        then: (schema) => schema.required(t('invoiceDataForm.validation.required')),
        otherwise: (schema) => schema,
      }),
    firstName: yup
      .string()
      .trim()
      .max(INVOICE_DATA_MAX_LENGTHS.firstName, ({ max }) => t('invoiceDataForm.validation.maxLength', { max }))
      .when('rfc', {
        is: (rfc: string) => getInvoicePersonType(rfc) === 'person',
        then: (schema) => schema.required(t('invoiceDataForm.validation.required')),
        otherwise: (schema) => schema,
      }),
    firstLastName: yup
      .string()
      .trim()
      .max(INVOICE_DATA_MAX_LENGTHS.firstLastName, ({ max }) => t('invoiceDataForm.validation.maxLength', { max }))
      .when('rfc', {
        is: (rfc: string) => getInvoicePersonType(rfc) === 'person',
        then: (schema) => schema.required(t('invoiceDataForm.validation.required')),
        otherwise: (schema) => schema,
      }),
    secondLastName: yup
      .string()
      .trim()
      .max(INVOICE_DATA_MAX_LENGTHS.secondLastName, ({ max }) => t('invoiceDataForm.validation.maxLength', { max })),
    postalCode: yup
      .string()
      .required(t('invoiceDataForm.validation.required'))
      .matches(POSTAL_CODE_REGEX, t('invoiceDataForm.validation.postalCodeFormat')),
    rfc: yup
      .string()
      .required(t('invoiceDataForm.validation.required'))
      .matches(RFC_REGEX, t('invoiceDataForm.validation.rfcFormat'))
      .test(
        'not-generic-rfc',
        t('invoiceDataForm.validation.rfcGeneric'),
        (value) => !value || validateTaxId(value, true).type !== rfcTypes.GENERIC,
      ),
    cfdi: yup.string().required(t('invoiceDataForm.validation.required')),
    email: yup
      .string()
      .required(t('invoiceDataForm.validation.required'))
      .email(t('invoiceDataForm.validation.emailFormat')),
    taxRegimeSatCode: yup.string().required(t('invoiceDataForm.validation.required')),
  });
